/* eslint-disable nimbus/prefer-until-destroyed */
import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { catchError, distinctUntilChanged, filter, skip, take } from 'rxjs/operators';
import { compareLocale } from '@nimbus-library/features/localization/utils/active-locale';
import { UserResponsibilityProfile } from '../models/client-settings';
import { AuthService } from './auth.service';
import { SettingsService } from './settings.service';
import { AssistantHub } from './signalr/assistant.hub';
import { LoggingService } from './logging.service';

@Injectable({
  providedIn: 'root',
})
export class ResponsibilityProfileService {
  public readonly profile$ = new BehaviorSubject<UserResponsibilityProfile>(undefined);
  public readonly responsibilityProfiles$ = new BehaviorSubject<UserResponsibilityProfile[]>([]);

  public constructor(
    private readonly hub: AssistantHub,
    private readonly settingsService: SettingsService,
    private readonly authService: AuthService,
    private readonly loggingService: LoggingService,
  ) {
    this.setupSubscriptions();
  }

  public updateProfiles(): void {
    this.hub
      .getResponsibilityProfiles()
      .pipe(
        catchError((error) => {
          this.loggingService.Error(`Error getting responsibility profiles: \n ${JSON.stringify(error)}`);
          this.loggingService.Info(`Settings polling will be restarted`);
          this.settingsService.restartPolling();
          return [];
        }),
      )
      .subscribe((profiles) => {
        this.resetProfiles(profiles);
      });
  }

  public changeResponsibilityProfile(id: string): void {
    const profile = this.responsibilityProfiles$.value.find((x) => x.id === id);
    this.hub.setResponsibilityProfile(profile);
  }

  private setupSubscriptions(): void {
    this.hub.onUserProfileListChanged$.subscribe((profiles) => {
      this.resetProfiles(profiles);
    });

    this.hub.isConnected$.pipe(filter((x) => x !== undefined)).subscribe((connected) => {
      if (!connected && this.profile$.value === undefined) {
        this.profile$.next(null);
      }

      if (connected && this.settingsService.hasContactCenterLicense$.value) {
        this.updateProfiles();
      }
    });

    combineLatest([
      this.authService.authenticated$.pipe(distinctUntilChanged()),
      this.settingsService.hasContactCenterLicense$.pipe(distinctUntilChanged()),
    ]).subscribe(([isAuthenticated, hasContactCenterLicense]) => {
      if (!isAuthenticated || !hasContactCenterLicense) {
        this.responsibilityProfiles$.next([]);
        this.profile$.next(undefined);
      }
    });
  }

  private resetProfiles(profiles: UserResponsibilityProfile[]): void {
    if (!profiles || !profiles.length) {
      return;
    }
    this.responsibilityProfiles$.next(profiles.sort((a, b) => compareLocale(a.name, b.name)));
    const newProfile = profiles.find((x) => x.active);
    if (this.profile$.value?.isDuty !== newProfile.isDuty) {
      this.settingsService.settings$.pipe(skip(1), take(1)).subscribe(() => this.profile$.next(newProfile));
      this.settingsService.restartPolling();
      return;
    }
    this.profile$.next(newProfile);
  }
}
